import { useMemo, useState, useCallback } from 'react';
import { FormLabel, Grid } from '@material-ui/core';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import BreadCrumbs from '../../../Core/Controls/Breadcrumb';
import ReportGijgoGrid from '../ReportsScreens/GijgoForReportsScreen';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { getReportsSalesDropdownsResolveData } from '../../../Core/Service/reportService';
import formatters from '../../../Core/Grid/inputFormatter';
import { DatePicker, SecondaryButton, SingelSelect, TextBox } from '../../../Core/FormInput';
import VehicleServiceHistory from './VehicleServiceHistory';
import DialogComp from '../../../Core/Modal/dialogModal';

// ✅ Utility debounce
const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

const baseColumns = [
    { field: 'vehicle', title: 'Vehicle', sortable: true, width: 130 },
    { field: 'dor', title: 'D.O.R', sortable: true, width: 100 },
    { field: 'lastSeen', title: 'Date Last Seen', sortable: true, width: 120 },
    { field: 'months', title: 'Months', sortable: true, width: 90, align: 'right' },
    { field: 'spend', title: 'Spend', sortable: true, renderer: formatters.CurrencyThousandSeparator, width: 100, align: 'right' },
    { field: 'latestMileage', title: 'Mileage (From New)', sortable: true, width: 140, align: 'right', renderer: formatters.ThousandSeparatorWithoutZero },
    { field: 'avG_PPM', title: 'AVG PPM', sortable: true, renderer: formatters.CurrencyThousandSeparator, width: 90, align: 'right' },
    { field: 'monthlyCostNew', title: 'Monthly Cost (from new)', sortable: true, renderer: formatters.CurrencyThousandSeparator, width: 170, align: 'right' },
    { field: 'annualCostNew', title: 'Annual Cost (from new)', sortable: true, renderer: formatters.CurrencyThousandSeparator, width: 170, align: 'right' },
    { field: 'iN_NTV_MILEAGE', title: 'IN NTV Mileage', sortable: true, width: 130, align: 'right', renderer: formatters.ThousandSeparatorWithoutZero },
    { field: 'avgppM_INNTV', title: 'AVG PPM (IN NTV)', sortable: true, width: 140, align: 'right' },
    { field: 'monthly', title: 'Monthly', sortable: true, renderer: formatters.CurrencyThousandSeparator, width: 90, align: 'right' }
];

const crumbs = () => [
    { name: 'Home', path: '/' },
    { name: 'Reports', active: true },
    { name: 'Vehicle Spend', active: true }
];

const useStyles = makeStyles(() => ({
    columnStyle: { cursor: 'pointer' }
}));

const VehicleSpendList = () => {
    const history = useHistory();
    const classes = useStyles();

    const [state, setState] = useState({
        userList: [],
        assignedUserID: null,
        errors: {},
        startDate: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        vehicleReg: '',
        VehicleServiceHistory: false,
        selectedRecord: {}
    });

    const [finalUrl, setFinalUrl] = useState(''); // ✅ Only updates when button clicked

    // ✅ Debounced state setter for inputs
    const debouncedSetState = useMemo(
        () =>
            debounce((newState) => {
                setState((st) => ({ ...st, ...newState }));
            }, 0),
        []
    );

    const handleInput = useCallback(
        (e) => {
            const { name, value } = e.target;
            debouncedSetState({ [name]: value });
        },
        [debouncedSetState]
    );

    const singleSelectChange = (nm) => (ev, val) => {
        debouncedSetState({ [nm]: val });
    };

    // ✅ Triggered only when button clicked
    const handleGetData = useCallback(() => {
        const url = `VehicleSpend?StartDate=${state.startDate}&EndDate=${state.endDate}&reg=${state.vehicleReg || ''}`;
        setFinalUrl(url);
    }, [state.startDate, state.endDate, state.vehicleReg]);

    // Vehicle reg no click handler
    const registrationLVUHandler = (value, record) => {
        const spn = document.createElement('span');
        const lnk = (
            <Grid
                onClick={() => setState((st) => ({ ...st, VehicleServiceHistory: true, selectedRecord: record }))}
                style={{ cursor: 'pointer', fontWeight: 'bold' }}
            >
                {record.vehicle}
            </Grid>
        );
        ReactDOM.render(lnk, spn);
        return spn;
    };

    const column = useMemo(() => {
        let col = [...baseColumns];
        let registrationCol = col.find((m) => m.field === 'vehicle');
        if (registrationCol) {
            registrationCol.renderer = registrationLVUHandler;
        }
        return col;
    }, []);

    return (
        <div className="report-screen-container">
            <BreadCrumbs crumbs={crumbs()} />
            <Grid container spacing={1} style={{ padding: '10px 0px' }}>
                <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <FormLabel style={{ marginBottom: 10 }}>Start Date</FormLabel>
                            <DatePicker placeholder="Start Date" name="startDate" fullWidth value={state.startDate} onChange={handleInput} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <FormLabel style={{ marginBottom: 10 }}>End Date</FormLabel>
                            <DatePicker placeholder="End Date" name="endDate" fullWidth value={state.endDate} onChange={handleInput} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2} style={{ marginBottom: -4 }}>
                            <FormLabel>Registration No</FormLabel>
                            <TextBox name="vehicleReg" onChange={handleInput} value={state.vehicleReg} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <SecondaryButton fullWidth onClick={handleGetData}>
                                Get Data
                            </SecondaryButton>
                        </Grid>
                    </Grid>
                </Grid>

                {finalUrl && (
                    <Grid item xs={12}>
                        <ReportGijgoGrid
                            dataConverter={(res) => ({
                                records: res.data.list,
                                total: res.data.total
                            })}
                            columns={column}
                            getUrl={finalUrl} // ✅ Only updates when user clicks Get Data
                            displayPagination={true}
                            downloadName={'Vehicle Spend'}
                        />
                    </Grid>
                )}
            </Grid>

            {state.VehicleServiceHistory && (
                <DialogComp
                    onClose={() => setState((st) => ({ ...st, VehicleServiceHistory: false, selectedRecord: {} }))}
                    maxWidth="md"
                    overflow="auto"
                    title={`Vehicle Service History - ${state.selectedRecord?.vehicle || ''}`}
                    fullWidth
                >
                    <VehicleServiceHistory
                        Vehicle={state.selectedRecord?.vehicle}
                        onClose={() => setState((st) => ({ ...st, VehicleServiceHistory: false, selectedRecord: {} }))}
                    />
                </DialogComp>
            )}
        </div>
    );
};

export default VehicleSpendList;
